import { useState } from "react";
import { Search, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GlassCard } from "@/components/ui/glass-card";
import { AppIcon } from "@/components/applications/AppIcon";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";
import { useAppIconLookup } from "@/hooks/useAppIconLookup";

interface TagApplicationsTabProps { tagId: string; }

export const TagApplicationsTab = ({ tagId }: TagApplicationsTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ["tag-apps", tagId],
    queryFn: () => tagsApi.getAppsByTag(tagId),
  });

  const iconMap = useAppIconLookup(apps);

  const filteredApps = apps.filter((a: any) =>
    (a.name || a.appName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (a.bundleId || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <GlassCard key={i} variant="subtle" className="p-4 animate-pulse">
            <div className="flex flex-col items-center space-y-3"><div className="w-12 h-12 rounded-xl bg-muted"></div></div>
          </GlassCard>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Search applications..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9" />
        </div>
      </div>
      {filteredApps.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No Applications Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">{searchQuery ? "No applications match your search." : "No applications assigned to this tag yet."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {filteredApps.map((app: any) => {
            const appId = app.id || app.appId || app.uuid;
            const appName = app.name || app.appName || 'Unknown App';
            const iconUrl = app.appIconUrl || app.iconUrl || app.icon_url || app.icon || app.artworkUrl512 || app.artworkUrl100 || app.artworkUrl60 || app.artworkUrl || app.artwork_url || app.app_icon_url || app.iconURL || app.imageUrl || app.image_url || (app.bundleId ? iconMap[app.bundleId] : null) || null;

            return (
              <GlassCard key={appId} variant="subtle" className="p-4">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden border">
                    <AppIcon iconSrc={iconUrl} appName={appName} className="w-12 h-12" />
                  </div>
                  <div className="space-y-1 w-full">
                    <Link to={`/applications/${appId}`} className="font-medium text-sm line-clamp-2 min-h-[2.5rem] flex items-center justify-center hover:text-primary transition-colors">
                      {appName}
                    </Link>
                    {app.bundleId && (
                      <p className="text-[10px] text-muted-foreground truncate" title={app.bundleId}>{app.bundleId}</p>
                    )}
                    <div className="flex items-center justify-center gap-1">
                      {app.version && <Badge variant="secondary" className="text-[10px] h-4">{app.version}</Badge>}
                      {app.platform && <Badge variant="outline" className="text-[10px] h-4 capitalize">{app.platform}</Badge>}
                    </div>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
};
